import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Dimensions,
} from 'react-native';
import { Delete, Check, Lock } from 'lucide-react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ParentStackParamList } from '../../navigation/types';
import { authApi } from '../../api/auth';
import { useAuthStore } from '../../state/authStore';
import { colors, fontFamily } from '../../theme';

type Props = NativeStackScreenProps<ParentStackParamList, 'PinSetup'>;

// Same phone/TV split as PinGate — RNV's getScaledValue assumes a 1080p stage,
// which rendered this keypad tiny on a 390pt phone.
const IS_PHONE = Platform.OS !== 'web' && Dimensions.get('window').width < 768;
const KEY_SIZE = IS_PHONE ? 66 : 74;
const KEY_GAP = IS_PHONE ? 12 : 14;
const DOT_GAP = 14;

// ─────────────────────────────────────────────────────────────────────────────
// Set-a-PIN, styled to match PinGate.
//
// These two screens are the same task ("enter 4 digits") moments apart in the
// same flow, but they had drifted into two different designs: this one drew
// small square keys with a grey confirm that never changed state, while PinGate
// had round keys, a lock badge and a confirm that fills in once four digits are
// in. Sharing the visual language (and the constants above) keeps them honest.
// The logic stays separate on purpose — setting a PIN and verifying one differ.
// ─────────────────────────────────────────────────────────────────────────────
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'del', '0', 'ok'];

export default function PinSetupScreen({ navigation, route }: Props) {
  const [pin, setPin] = useState('');
  const { accessToken } = useAuthStore();
  // ProfileSelect → "Add a child" passes nextRoute='AddChild' so the parent
  // lands on the add-child screen after setting (or skipping) the PIN. The
  // default ParentHome preserves the Me → PIN entry point.
  const nextRoute = route.params?.nextRoute ?? 'ParentHome';

  const handleKeyPress = (key: string) => {
    if (key === 'del') {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (pin.length < 4) setPin((p) => p + key);
  };

  const handleSave = async () => {
    if (pin.length < 4) {
      Alert.alert('PIN must be 4 digits');
      return;
    }
    try {
      await authApi.setPin(pin, accessToken!);
      navigation.replace(nextRoute);
    } catch {
      Alert.alert('Failed to save PIN');
    }
  };

  const skip = () => navigation.replace(nextRoute);
  const ready = pin.length === 4;

  return (
    <View style={styles.container}>
      <View style={styles.badge}>
        <Lock size={22} color={colors.primaryInk} strokeWidth={2.4} />
      </View>

      <Text style={styles.title}>Set a Parent PIN</Text>
      {/* The old copy said "Used to access parent settings on the TV", which is
          wrong on a phone: the same PIN gates the parent area and the kid-mode
          exit right here. */}
      <Text style={styles.subtitle}>
        Unlocks the parent area and lets you leave Kid Mode — on this phone and
        on your TV.
      </Text>

      <View style={styles.dots}>
        {[0, 1, 2, 3].map((i) => (
          <View
            key={i}
            style={[styles.dot, pin.length > i && styles.dotFilled]}
          />
        ))}
      </View>

      <View style={styles.keypad}>
        {KEYS.map((k) => {
          const isOk = k === 'ok';
          return (
            <TouchableOpacity
              key={k}
              style={[
                styles.key,
                isOk && ready && styles.keyOk,
              ]}
              activeOpacity={0.65}
              accessibilityRole="button"
              accessibilityLabel={
                k === 'del' ? 'Delete' : isOk ? 'Save PIN' : `Number ${k}`
              }
              onPress={() => (isOk ? handleSave() : handleKeyPress(k))}
            >
              {k === 'del' ? (
                <Delete size={22} color={colors.ink2} strokeWidth={2.2} />
              ) : isOk ? (
                <Check
                  size={24}
                  color={ready ? '#fff' : colors.ink3}
                  strokeWidth={3}
                />
              ) : (
                <Text style={styles.keyText}>{k}</Text>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      <TouchableOpacity onPress={skip} style={styles.skipBtn}>
        <Text style={styles.skip}>Skip for now</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFF8F0',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 28,
  },
  badge: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bgWarm,
    marginBottom: 14,
  },
  title: {
    fontFamily,
    fontSize: 22,
    fontWeight: '900',
    color: colors.ink,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily,
    fontSize: 14,
    fontWeight: '600',
    color: colors.ink3,
    textAlign: 'center',
    marginTop: 6,
    marginBottom: 24,
    paddingHorizontal: 12,
    lineHeight: 20,
  },
  // No flex `gap` here either — symmetric margins, same reasoning as PinGate.
  dots: { flexDirection: 'row', marginBottom: 26 },
  dot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    marginHorizontal: DOT_GAP / 2,
    borderWidth: 2,
    borderColor: 'rgba(46,35,22,0.22)',
    backgroundColor: 'transparent',
  },
  dotFilled: { backgroundColor: colors.primary, borderColor: colors.primary },
  keypad: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: (KEY_SIZE + KEY_GAP) * 3,
  },
  key: {
    width: KEY_SIZE,
    height: KEY_SIZE,
    margin: KEY_GAP / 2,
    borderRadius: KEY_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: colors.lineStrong,
  },
  keyText: {
    fontFamily,
    fontSize: 26,
    fontWeight: '700',
    color: colors.ink,
    fontVariant: ['tabular-nums'],
  },
  // Confirm fills in only once four digits are in, so the primary action is
  // unmistakable — the old screen's grey tick looked disabled at every stage.
  keyOk: { backgroundColor: colors.primary, borderColor: colors.primary },
  skipBtn: { marginTop: 22, paddingVertical: 8, paddingHorizontal: 16 },
  skip: {
    fontFamily,
    fontSize: 14,
    fontWeight: '700',
    color: colors.ink3,
  },
});
